import { logClickEvent, logGenericEvent, } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { logError, logFeature, logUserAction, } from '@office-start/telemetry-service';
import { getReferral } from '@office-start/telemetry-utility';
import { contextToEventMapper } from './contextToEventMapper';
/**
 * This class defines a host-app specific logger for Office.com.
 *
 * It takes events from SERP and does some custom mapping and logging.
 */
var SearchHostAppLogger = /** @class */ (function () {
    function SearchHostAppLogger() {
        this.sessionExpirationTimeInMilliseconds = 60000; // 60 seconds
        this.sessionStarted = false;
        this.searchCenterUrlReferral = null;
        this.OneDriveBusinessUrlRegex = /http[s]?:\/\/[\S]*?-my\.sharepoint[\S]*?\.com/;
        this.SharePointUrlRegex = /http[s]?:\/\/[\S]*?\.sharepoint[\S]*?\.com/;
        this.OneDriveConsumerUrlRegex = /http[s]?:\/\/d\.docs\.live\.net/;
        // empty
    }
    SearchHostAppLogger.prototype.searchLogger = function (_a) {
        var _b;
        var logEvent = _a.logEvent, referral = _a.referral, referralPlatform = _a.referralPlatform, usingOneDriveResourceAsSharePointResource = _a.usingOneDriveResourceAsSharePointResource, isMsa = _a.isMsa;
        var fastEventFunction = contextToEventMapper[logEvent.nameDetail || logEvent.name];
        var fastEvent = fastEventFunction === null || fastEventFunction === void 0 ? void 0 : fastEventFunction[0];
        if (!fastEventFunction || (!isMsa && !fastEvent)) {
            return;
        }
        if (isMsa) {
            return this.msaSearchLogger({
                logEvent: logEvent,
                referral: referral,
                referralPlatform: referralPlatform,
            });
        }
        this.checkForSessionTimeout();
        var logProperties = logEvent.properties || {};
        var params = {};
        params["ItemCategory" /* InstrumentationParam.ItemCategory */] = fastEvent.itemCategory;
        params["SearchResultType" /* InstrumentationParam.SearchResultType */] =
            fastEvent.searchResultType;
        if (usingOneDriveResourceAsSharePointResource !== undefined) {
            params["UsingSharePointResourceId" /* InstrumentationParam.UsingSharePointResourceId */] =
                usingOneDriveResourceAsSharePointResource.toString();
        }
        if (fastEvent.event == KnownInstrumentationEvents.SearchResultsFetched) {
            try {
                params["SearchItemCount" /* InstrumentationParam.SearchItemCount */] =
                    (logProperties === null || logProperties === void 0 ? void 0 : logProperties.items)
                        ? (_b = JSON.parse(logProperties === null || logProperties === void 0 ? void 0 : logProperties.items)) === null || _b === void 0 ? void 0 : _b.length
                        : null;
            }
            catch (e) {
                var errorMessage = void 0;
                if (typeof e === 'string') {
                    errorMessage = e;
                }
                else if (e instanceof Error) {
                    errorMessage = e.message;
                }
                logError({
                    eventName: 'Event_Properties_Parse_Error',
                    message: errorMessage,
                });
            }
        }
        if (fastEvent.event === KnownInstrumentationEvents.SearchClick) {
            var id = "".concat(fastEvent.itemCategory, "_").concat(fastEvent.searchResultType);
            var otelReferral = referral !== null && referral !== void 0 ? referral : this.getReferralFromEventData(logProperties.eventData, fastEvent);
            logClickEvent(fastEvent.event, id, fastEvent.knownArea, otelReferral, referralPlatform, params);
            logUserAction({
                eventName: fastEvent.event,
                id: id,
                area: fastEvent.knownArea,
                result: otelReferral ? 'Refer' : 'Navigate',
                target: otelReferral ? getReferral(otelReferral) : 'Other',
            });
        }
        else {
            logGenericEvent(fastEvent.event, params);
        }
    };
    SearchHostAppLogger.prototype.setSearchCenterUrlReferral = function (searchCenterUrl) {
        if (searchCenterUrl && searchCenterUrl.length) {
            if (searchCenterUrl.match(this.OneDriveBusinessUrlRegex) ||
                searchCenterUrl.match(this.OneDriveConsumerUrlRegex)) {
                this.searchCenterUrlReferral =
                    "Documents" /* KnownWorkloadReferralNames.OneDrive */;
            }
            if (searchCenterUrl.match(this.SharePointUrlRegex)) {
                this.searchCenterUrlReferral =
                    "Sites" /* KnownWorkloadReferralNames.SharePoint */;
            }
        }
    };
    SearchHostAppLogger.prototype.msaSearchLogger = function (_a) {
        var _this = this;
        var _b, _c;
        var logEvent = _a.logEvent, referral = _a.referral, referralPlatform = _a.referralPlatform;
        var logProperties = logEvent.properties || {};
        var fastEventFunction = contextToEventMapper[logEvent.nameDetail || logEvent.name];
        var fastEvents = fastEventFunction({
            filterId: (_b = logProperties.options) === null || _b === void 0 ? void 0 : _b.RefinerId,
            verticalId: logProperties.VerticalId || ((_c = logProperties.options) === null || _c === void 0 ? void 0 : _c.itemKey),
        });
        fastEvents.forEach(function (fastEvent) {
            var params = {};
            params["SearchProviderType" /* InstrumentationParam.SearchProviderType */] =
                fastEvent.searchProviderType;
            params["ItemCategory" /* InstrumentationParam.ItemCategory */] = fastEvent.itemCategory;
            params["SearchResultType" /* InstrumentationParam.SearchResultType */] =
                fastEvent.searchResultType;
            switch (fastEvent.itemCategory) {
                case "Data_Fetch_Latency" /* SearchResultClickType.DataFetchLatency */:
                    params["Latency" /* InstrumentationParam.Latency */] =
                        logProperties === null || logProperties === void 0 ? void 0 : logProperties.DataFetchLatency;
                    break;
                case "E2E_Latency" /* SearchResultClickType.E2ELatency */:
                    params["Latency" /* InstrumentationParam.Latency */] =
                        logProperties === null || logProperties === void 0 ? void 0 : logProperties.E2ELatency;
                    break;
            }
            if (fastEvent.itemCategory === "Result" /* SearchResultClickType.Result */) {
                logClickEvent(fastEvent.event, fastEvent.elementId || '', fastEvent.knownArea, referral
                    ? referral
                    : _this.getReferralFromEventData(logProperties.eventData), referralPlatform, params);
                logUserAction({
                    id: fastEvent.elementId || '',
                    area: fastEvent.knownArea,
                    result: referral ? 'Refer' : 'Navigate',
                    target: referral ? getReferral(referral) : 'Other',
                });
                // Put Clicks related properties to Feature event for Search team scorecarding
                params["Area" /* InstrumentationParam.Area */] = fastEvent.knownArea;
                params["ElementId" /* InstrumentationParam.ElementId */] = fastEvent.elementId;
                params["Referral" /* InstrumentationParam.Referral */] = referral
                    ? referral
                    : _this.getReferralFromEventData(logProperties.eventData);
                params["ReferralPlatform" /* InstrumentationParam.ReferralPlatform */] =
                    referralPlatform;
                logFeature({
                    featureName: "Search" /* KnownFeatureNames.Search */,
                    eventName: fastEvent.event,
                    params: params,
                });
            }
            else {
                params["Area" /* InstrumentationParam.Area */] = fastEvent.knownArea;
                logGenericEvent(fastEvent.event, params);
                logFeature({
                    featureName: "Search" /* KnownFeatureNames.Search */,
                    eventName: fastEvent.event,
                    params: params,
                });
            }
        });
    };
    SearchHostAppLogger.prototype.checkForSessionTimeout = function () {
        var _this = this;
        if (!this.sessionStarted) {
            this.sessionStarted = true;
            var params = {};
            params["ReferralType" /* InstrumentationParam.ReferralType */] =
                this.searchCenterUrlReferral;
            logGenericEvent(KnownInstrumentationEvents.SearchSessionStarted, params);
        }
        clearTimeout(this.sessionTimeout);
        this.sessionTimeout = setTimeout(function () {
            _this.sessionStarted = false;
        }, this.sessionExpirationTimeInMilliseconds);
    };
    SearchHostAppLogger.prototype.getReferralFromEventData = function (eventData, event) {
        if (event) {
            if (event.searchResultType === "Person" /* SearchResultType.Person */) {
                return "People" /* OtherKnownReferralNames.People */;
            }
            if (event.searchResultType === "Site" /* SearchResultType.Site */ ||
                event.searchResultType === "News" /* SearchResultType.News */) {
                return "Sites" /* KnownWorkloadReferralNames.SharePoint */;
            }
        }
        if (!eventData) {
            return '';
        }
        eventData = eventData.toLocaleLowerCase();
        if (eventData.indexOf('excel') !== -1) {
            return "ExcelOnline" /* KnownWorkloadReferralNames.Excel */;
        }
        if (eventData.indexOf('powerpoint') !== -1) {
            return "PowerPointOnline" /* KnownWorkloadReferralNames.PowerPoint */;
        }
        if (eventData.indexOf('word') !== -1) {
            return "WordOnline" /* KnownWorkloadReferralNames.Word */;
        }
        if (eventData.indexOf('onenote') !== -1) {
            return "OneNoteOnline" /* KnownWorkloadReferralNames.OneNote */;
        }
        if (eventData.indexOf('pdf') !== -1) {
            return "PDF" /* OtherKnownReferralNames.PDF */;
        }
        return '';
    };
    return SearchHostAppLogger;
}());
export { SearchHostAppLogger };
//# sourceMappingURL=SearchHostAppLogger.js.map